import { t } from '../i18n.js';
import { chevronDown } from './icons.js';
import { iconProfile, iconLogout } from './brand-icons.js';

/**
 * GitHub-style user menu: a trigger button (avatar + name + caret) that
 * toggles a dropdown panel containing an identity header, a Profile
 * link, and a Log out button.
 *
 * The avatar (trigger + panel header) is user-picked content and stays
 * emoji per brand-iconography; every other menu-row icon (Profile,
 * `extraItems`, Log out) is app chrome and renders a brand-icons.js SVG.
 *
 * `extraItems` lets callers inject additional menu rows between Profile and
 * Log out. Each item can opt into responsive hiding via
 * `hiddenAtBreakpoint: 'sm'`, which adds `sm:hidden` — used on mobile to
 * surface admin destinations (Units, Users) that live in the header tab
 * row on desktop.
 *
 * @param {object} props
 * @param {string} props.name   Display name (already resolved to a fallback by caller if empty).
 * @param {string} props.icon   Emoji avatar (caller passes a fallback like '👤' if null).
 * @param {string} props.email  User email; shown in the panel header.
 * @param {() => (void | Promise<void>)} props.onLogout Called when the Log out item is clicked.
 * @param {Array<{ href: string, iconFn: (extraClass?: string) => SVGSVGElement, labelKey: string, hiddenAtBreakpoint?: 'sm' }>} [props.extraItems]
 *   Additional menu rows between Profile and Log out. Pass `[]` or omit to skip.
 * @returns {{ el: HTMLElement }}
 */
export function userMenu({ name, icon, email, onLogout, extraItems = [], localeControl = null }) {
  const wrap = document.createElement('div');
  wrap.className = 'relative';

  // ----- Trigger ----------------------------------------------------------
  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.setAttribute('data-user-menu-trigger', '');
  trigger.setAttribute('aria-haspopup', 'menu');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-label', t('user_menu.open_aria'));
  trigger.className = [
    'inline-flex items-center gap-2 px-2 py-1 rounded-full',
    'text-ink dark:text-ink-on-dark hover:bg-primary-50 dark:hover:bg-white/10',
    'focus:outline-none focus:ring-2 focus:ring-primary-500/50',
  ].join(' ');

  const avatar = document.createElement('span');
  avatar.className = [
    'inline-flex items-center justify-center w-7 h-7 rounded-full',
    'bg-primary-50 border border-border dark:bg-primary-900/40 dark:border-border-dark',
    'text-lg leading-none',
  ].join(' ');
  avatar.textContent = icon;

  const nameEl = document.createElement('span');
  nameEl.className = 'text-sm truncate max-w-[10rem] text-ink-muted';
  nameEl.textContent = name;

  const caret = chevronDown('opacity-60');

  trigger.append(avatar, nameEl, caret);
  wrap.append(trigger);

  // ----- Panel ------------------------------------------------------------
  const panel = document.createElement('div');
  panel.setAttribute('data-user-menu-panel', '');
  panel.setAttribute('role', 'menu');
  panel.className = [
    'hidden absolute right-0 mt-2 z-50 min-w-[240px] anim-pop',
    'glass-panel rounded-lg',
    'p-1.5 text-sm text-ink dark:text-ink-on-dark',
  ].join(' ');

  // Identity header
  const header = document.createElement('div');
  header.className = 'flex items-center gap-3 p-3';

  const headerAvatar = document.createElement('span');
  headerAvatar.className = [
    'inline-flex items-center justify-center w-9 h-9 rounded-full',
    'bg-surface dark:bg-surface-dark border border-border dark:border-border-dark',
    'text-xl leading-none',
  ].join(' ');
  headerAvatar.textContent = icon;

  const headerText = document.createElement('div');
  headerText.className = 'min-w-0';
  const headerName = document.createElement('div');
  headerName.className = 'font-semibold truncate';
  headerName.textContent = name;
  const headerEmail = document.createElement('div');
  headerEmail.className = 'text-xs text-ink-muted truncate';
  headerEmail.textContent = email;
  headerText.append(headerName, headerEmail);

  header.append(headerAvatar, headerText);
  panel.append(header);

  const divider1 = document.createElement('div');
  divider1.className = 'h-px bg-border dark:bg-border-dark my-1';
  panel.append(divider1);

  // Profile item
  const profileLink = document.createElement('a');
  profileLink.href = '#/profile';
  profileLink.setAttribute('role', 'menuitem');
  profileLink.className = 'flex items-center gap-2 px-3 py-2 rounded hover-glow';
  const profileIcon = iconProfile();
  const profileLabel = document.createElement('span');
  profileLabel.textContent = t('nav.profile');
  profileLink.append(profileIcon, profileLabel);
  profileLink.addEventListener('click', () => close());
  panel.append(profileLink);

  // Caller-supplied items (e.g. admin tabs on mobile). Rendered between
  // Profile and the divider preceding Log out.
  if (Array.isArray(extraItems) && extraItems.length > 0) {
    for (const item of extraItems) {
      const a = document.createElement('a');
      a.href = item.href;
      a.setAttribute('role', 'menuitem');
      a.dataset.role = 'user-menu-extra-item';
      a.dataset.labelKey = item.labelKey;
      const baseClasses = ['flex items-center gap-2 px-3 py-2 rounded hover-glow'];
      if (item.hiddenAtBreakpoint === 'sm') baseClasses.push('sm:hidden');
      a.className = baseClasses.join(' ');
      const ic = item.iconFn();
      const lbl = document.createElement('span');
      lbl.textContent = t(item.labelKey);
      a.append(ic, lbl);
      a.addEventListener('click', () => close());
      panel.append(a);
    }
  }

  // Locale switcher. It lives here rather than in the header bar because it is
  // a rarely-touched personal preference, not chrome — and because it was the
  // only native <select> in the header, which made every `querySelector('select')`
  // in a test a silent trap for whatever control landed there next.
  if (localeControl) {
    const localeDivider = document.createElement('div');
    localeDivider.className = 'h-px bg-border dark:bg-border-dark my-1';
    panel.append(localeDivider);

    const localeRow = document.createElement('div');
    localeRow.dataset.role = 'user-menu-locale';
    localeRow.className = 'flex items-center justify-between gap-3 px-3 py-2';

    const localeLabel = document.createElement('span');
    localeLabel.className = 'text-sm';
    localeLabel.textContent = t('nav.language');

    localeRow.append(localeLabel, localeControl);
    panel.append(localeRow);
  }

  const divider2 = document.createElement('div');
  divider2.className = 'h-px bg-border dark:bg-border-dark my-1';
  panel.append(divider2);

  // Log out item
  const logoutBtn = document.createElement('button');
  logoutBtn.type = 'button';
  logoutBtn.setAttribute('data-user-menu-logout', '');
  logoutBtn.setAttribute('role', 'menuitem');
  logoutBtn.className = 'flex items-center gap-2 px-3 py-2 rounded w-full text-left hover-glow';
  const logoutIcon = iconLogout();
  const logoutLabel = document.createElement('span');
  logoutLabel.textContent = t('nav.logout');
  logoutBtn.append(logoutIcon, logoutLabel);
  logoutBtn.addEventListener('click', async () => {
    close();
    await onLogout();
  });
  panel.append(logoutBtn);

  wrap.append(panel);

  // ----- State + handlers -------------------------------------------------
  let open = false;
  let onDocMouseDown = null;
  let onKeyDown = null;
  let onHashChange = null;

  function openPanel() {
    if (open) return;
    open = true;
    panel.classList.remove('hidden');
    trigger.setAttribute('aria-expanded', 'true');

    onDocMouseDown = (ev) => {
      if (!wrap.contains(ev.target)) close();
    };
    onKeyDown = (ev) => {
      if (ev.key === 'Escape') {
        close();
        trigger.focus();
      }
    };
    onHashChange = () => close();

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('hashchange', onHashChange);
  }

  function close() {
    if (!open) return;
    open = false;
    panel.classList.add('hidden');
    trigger.setAttribute('aria-expanded', 'false');

    if (onDocMouseDown) document.removeEventListener('mousedown', onDocMouseDown);
    if (onKeyDown) document.removeEventListener('keydown', onKeyDown);
    if (onHashChange) window.removeEventListener('hashchange', onHashChange);
    onDocMouseDown = onKeyDown = onHashChange = null;
  }

  trigger.addEventListener('click', () => {
    if (open) close();
    else openPanel();
  });

  /**
   * Release the document- and window-level listeners this menu installs while
   * open. `close()` already removes them, but a shell remount discards the DOM
   * node without closing the panel first — which orphaned three listeners per
   * open menu on every locale switch, login and profile save.
   */
  function destroy() {
    close();
  }

  return { el: wrap, destroy };
}
